import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useXP } from "@/hooks/useXP";
import { useHealth } from "@/hooks/useHealth";
import { useChallenges } from "@/hooks/useChallenges";
import { getCompletedLevels, getLevelBestScore, LEVELS } from "@/config/levels";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  X,
  Star,
  Trophy,
  Target,
  Zap,
  Heart,
  Crown,
  Settings,
  Newspaper,
  Scale,
  LogOut,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Sparkles,
  Flame,
  Gift,
  Palette,
  RotateCw,
  Lock,
  Check
} from "lucide-react";

// Prestige emblem configuration
const getPrestigeEmblem = (prestigeLevel) => {
  if (prestigeLevel >= 6) {
    return {
      symbol: 'M',
      name: 'Prestige Master',
      bgGradient: 'from-amber-400 via-yellow-300 to-amber-500',
      textColor: 'text-amber-900',
      borderColor: 'border-amber-300',
      shadowColor: 'shadow-amber-400/50',
      animate: 'animate-pulse'
    };
  }
  
  const emblems = {
    0: null,
    1: { symbol: 'I', name: 'Prestige I', bgGradient: 'from-emerald-500 to-green-600', textColor: 'text-white', borderColor: 'border-emerald-300' },
    2: { symbol: 'II', name: 'Prestige II', bgGradient: 'from-sky-500 to-blue-600', textColor: 'text-white', borderColor: 'border-sky-300' },
    3: { symbol: 'III', name: 'Prestige III', bgGradient: 'from-violet-500 to-purple-600', textColor: 'text-white', borderColor: 'border-violet-300' },
    4: { symbol: 'IV', name: 'Prestige IV', bgGradient: 'from-rose-500 to-pink-600', textColor: 'text-white', borderColor: 'border-rose-300' },
    5: { symbol: 'V', name: 'Prestige V', bgGradient: 'from-orange-500 to-red-600', textColor: 'text-white', borderColor: 'border-orange-300' },
  };
  
  return emblems[prestigeLevel] || null;
};

// Fruit Detail Modal Component with Level Rewards
function FruitDetailModal({ fruit, onClose }) {
  if (!fruit) return null;
  
  const xpPerLevel = 1000;
  const currentLevelXP = fruit.xp % xpPerLevel;
  const xpForNextLevel = xpPerLevel;
  const xpProgress = (currentLevelXP / xpForNextLevel) * 100;
  
  // Calculate current bonus based on level
  const getCurrentBonus = (level) => {
    return Math.floor(level / 10) * 5;
  };
  
  const currentBonus = getCurrentBonus(fruit.level);
  
  // Level milestones and rewards
  const LEVEL_REWARDS = [
    { level: 10, bonus: 5, icon: '🥉', title: 'Bronze Mastery' },
    { level: 20, bonus: 10, icon: '🥈', title: 'Silver Mastery' },
    { level: 30, bonus: 15, icon: '🥇', title: 'Gold Mastery' },
    { level: 40, bonus: 20, icon: '💎', title: 'Diamond Mastery' },
    { level: 50, bonus: 25, icon: '👑', title: 'Royal Mastery' },
    { level: 60, bonus: 30, icon: '🌟', title: 'Star Mastery' },
    { level: 70, bonus: 35, icon: '⚡', title: 'Lightning Mastery' },
    { level: 80, bonus: 40, icon: '🔥', title: 'Fire Mastery' },
    { level: 90, bonus: 45, icon: '🌈', title: 'Rainbow Mastery' },
    { level: 100, bonus: 50, icon: '🏆', title: 'Ultimate Mastery' },
  ];
  
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      <div className="relative w-full max-w-sm bg-gradient-to-b from-slate-800 to-slate-900 rounded-2xl border border-white/10 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 relative p-6 pb-4 bg-gradient-to-br from-orange-500/20 to-red-500/20 border-b border-white/10">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="w-4 h-4 text-white/70" />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-yellow-400/20 to-orange-500/20 flex items-center justify-center text-5xl border border-white/10">
              {fruit.emoji}
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white capitalize">{fruit.name}</h3>
              <p className="text-orange-400 font-semibold">Level {fruit.level}</p>
              {currentBonus > 0 && (
                <p className="text-green-400 text-sm font-medium">+{currentBonus} score bonus</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="p-6 space-y-4">
          {/* XP Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">XP Progress</span>
              <span className="text-white font-medium">
                {currentLevelXP.toLocaleString()} / {xpForNextLevel.toLocaleString()}
              </span>
            </div>
            <Progress value={xpProgress} className="h-3 bg-slate-700" />
            <p className="text-xs text-slate-500">
              Total XP: {(fruit.xp || 0).toLocaleString()}
            </p>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div className="p-3 rounded-xl bg-white/5 border border-white/10">
              <div className="flex items-center gap-2 text-yellow-400">
                <Star className="w-4 h-4" />
                <span className="text-sm font-medium">Matches</span>
              </div>
              <p className="text-xl font-bold text-white mt-1">
                {(fruit.matches || 0).toLocaleString()}
              </p>
            </div>
            <div className="p-3 rounded-xl bg-white/5 border border-white/10">
              <div className="flex items-center gap-2 text-emerald-400">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">Combos</span>
              </div>
              <p className="text-xl font-bold text-white mt-1">
                {(fruit.combos || 0).toLocaleString()}
              </p>
            </div>
          </div>
          
          {/* Level Rewards Section */}
          <div className="pt-4">
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
              <Gift className="w-4 h-4 text-purple-400" />
              Level Rewards
            </h4>
            
            <div className="space-y-2">
              {LEVEL_REWARDS.map((reward) => {
                const isUnlocked = fruit.level >= reward.level;
                const isNext = !isUnlocked && fruit.level < reward.level && 
                  (LEVEL_REWARDS.findIndex(r => r.level === reward.level) === 0 || 
                   fruit.level >= LEVEL_REWARDS[LEVEL_REWARDS.findIndex(r => r.level === reward.level) - 1]?.level);
                
                return (
                  <div 
                    key={reward.level}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-xl border transition-all",
                      isUnlocked 
                        ? "bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-500/30" 
                        : isNext
                          ? "bg-gradient-to-r from-orange-500/10 to-yellow-500/10 border-orange-500/30"
                          : "bg-white/5 border-white/10 opacity-50"
                    )}
                  >
                    <div className={cn(
                      "w-10 h-10 rounded-lg flex items-center justify-center text-xl",
                      isUnlocked ? "bg-green-500/20" : "bg-white/10"
                    )}>
                      {isUnlocked ? reward.icon : <Lock className="w-4 h-4 text-slate-500" />}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          "font-medium text-sm",
                          isUnlocked ? "text-white" : "text-slate-400"
                        )}>
                          {reward.title}
                        </span>
                        {isUnlocked && (
                          <Check className="w-4 h-4 text-green-400" />
                        )}
                      </div>
                      <p className={cn(
                        "text-xs",
                        isUnlocked ? "text-green-300" : "text-slate-500"
                      )}>
                        Level {reward.level} • +{reward.bonus} score per match
                      </p>
                    </div>
                    
                    {isNext && (
                      <div className="text-xs text-orange-400 font-medium">
                        {reward.level - fruit.level} lvls
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        <div className="p-4 pt-0">
          <Button
            onClick={onClose}
            className="w-full bg-white/10 hover:bg-white/20 text-white"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ProfileSidebar({ 
  isOpen, 
  onClose,
  onMenuSelect,
  currentStars = 0,
  canSpin = false,
  challengeCount = 0
}) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { 
    totalXP, 
    lifetimeXP, 
    level, 
    currentXP, 
    neededXP, 
    prestige,
    isMaxLevel,
  } = useXP();
  const { hp, maxHP } = useHealth();
  const { getFruitLevels } = useChallenges();
  
  const [showFruitMastery, setShowFruitMastery] = useState(false);
  const [showLegal, setShowLegal] = useState(false);
  const [selectedFruit, setSelectedFruit] = useState(null);
  
  const fruitLevels = getFruitLevels();
  const completedLevels = getCompletedLevels();
  const totalLevels = LEVELS.length;
  
  // Calculate total stars earned
  const totalStars = completedLevels.reduce((acc, levelId) => {
    const score = getLevelBestScore(levelId);
    const levelData = LEVELS.find(l => l.id === levelId);
    if (!levelData) return acc;
    const baseScore = levelData.targetCount * 10;
    if (score >= baseScore * 3) return acc + 3;
    if (score >= baseScore * 2) return acc + 2;
    if (score >= baseScore) return acc + 1;
    return acc;
  }, 0);
  
  // Get total score
  const totalScore = LEVELS.reduce((acc, lvl) => acc + getLevelBestScore(lvl.id), 0);
  
  const prestigeEmblem = getPrestigeEmblem(prestige);
  const xpProgress = neededXP > 0 ? (currentXP / neededXP) * 100 : 100;
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleFruitClick = (fruit) => {
    setSelectedFruit(fruit);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Fruit Detail Modal */}
      {selectedFruit && (
        <FruitDetailModal 
          fruit={selectedFruit} 
          onClose={() => setSelectedFruit(null)} 
        />
      )}
      
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[55] transition-opacity duration-300"
        onClick={onClose}
        data-testid="profile-sidebar-backdrop"
      />
      
      {/* Sidebar Panel */}
      <aside 
        className={cn(
          "fixed left-0 top-0 h-full w-80 z-[60]",
          "bg-gradient-to-b from-slate-900/98 via-slate-900/95 to-slate-800/98",
          "backdrop-blur-xl border-r border-white/10",
          "shadow-2xl shadow-black/50",
          "flex flex-col",
          "animate-in slide-in-from-left duration-300"
        )}
        data-testid="profile-sidebar"
      >
        {/* Header with Avatar - Fixed */}
        <div className="relative p-6 pb-4 border-b border-white/10 flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="close-profile-sidebar"
          >
            <X className="w-5 h-5 text-white/70" />
          </button>
          
          {/* Avatar */}
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-4xl shadow-lg shadow-orange-500/30 border-2 border-orange-300/50">
                🍌
              </div>
              <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold border-2 border-slate-900 shadow-lg">
                {level}
              </div>
            </div>
            
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-white truncate">
                {user?.playerName || user?.player_name || 'Player'}
              </h2>
              
              {prestigeEmblem && (
                <div className={cn(
                  "inline-flex items-center gap-1.5 px-2 py-1 rounded-full mt-1",
                  "bg-gradient-to-r",
                  prestigeEmblem.bgGradient,
                  "border",
                  prestigeEmblem.borderColor,
                  prestigeEmblem.animate
                )}>
                  <Crown className="w-3 h-3" />
                  <span className={cn("text-xs font-bold", prestigeEmblem.textColor)}>
                    {prestigeEmblem.name}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {/* XP Progress */}
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400 flex items-center gap-1">
                <Zap className="w-4 h-4 text-yellow-400" />
                Level {level}
              </span>
              <span className="text-slate-400">
                {isMaxLevel ? 'MAX' : `${currentXP.toLocaleString()} / ${neededXP.toLocaleString()} XP`}
              </span>
            </div>
            <Progress 
              value={xpProgress} 
              className="h-3 bg-slate-700/50"
            />
          </div>
        </div>
        
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
          {/* Quick Stats Grid */}
          <div className="p-4 border-b border-white/10">
            <div className="grid grid-cols-3 gap-3">
              {/* Challenges - Clickable */}
              <button
                onClick={() => {
                  onClose();
                  onMenuSelect?.('challenges');
                }}
                className="rounded-xl bg-gradient-to-br from-yellow-500/20 to-amber-500/20 border border-white/10 p-2 hover:from-yellow-500/30 hover:to-amber-500/30 transition-colors text-left"
                data-testid="challenges-stat"
              >
                <div className="flex items-center gap-1.5">
                  <Target className="w-4 h-4 text-yellow-400" />
                  <span className="font-bold text-white text-sm">
                    {challengeCount > 0 ? challengeCount : '→'}
                  </span>
                </div>
                <span className="text-slate-400 mt-0.5 block text-[10px]">
                  Challenges
                </span>
              </button>
              
              <StatCard 
                icon={<Trophy className="w-4 h-4 text-orange-400" />}
                value={totalScore.toLocaleString()}
                label="Best Score"
                color="from-orange-500/20 to-red-500/20"
              />
              <StatCard 
                icon={<Star className="w-4 h-4 text-emerald-400" />}
                value={`${completedLevels.length}/${totalLevels}`}
                label="Levels"
                color="from-emerald-500/20 to-green-500/20"
              />
            </div>
            
            {/* Additional Stats Row */}
            <div className="grid grid-cols-2 gap-3 mt-3">
              <StatCard 
                icon={<Sparkles className="w-4 h-4 text-purple-400" />}
                value={lifetimeXP.toLocaleString()}
                label="Lifetime XP"
                color="from-purple-500/20 to-indigo-500/20"
                small
              />
              <StatCard 
                icon={<Heart className="w-4 h-4 text-red-400" />}
                value={`${hp}/${maxHP}`}
                label="Health"
                color="from-red-500/20 to-pink-500/20"
                small
              />
            </div>
          </div>
          
          {/* Fruit Mastery (Collapsible) */}
          <div className="border-b border-white/10">
            <button
              onClick={() => setShowFruitMastery(!showFruitMastery)}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
              data-testid="fruit-mastery-toggle"
            >
              <span className="flex items-center gap-2 text-white font-medium">
                <Flame className="w-5 h-5 text-orange-400" />
                Fruit Mastery
              </span>
              {showFruitMastery ? (
                <ChevronUp className="w-5 h-5 text-slate-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              )}
            </button>
            
            {showFruitMastery && (
              <div className="px-4 pb-4 space-y-2 animate-in slide-in-from-top-2 duration-200">
                {fruitLevels && fruitLevels.length > 0 ? (
                  fruitLevels.map((fruit) => (
                    <button
                      key={fruit.id}
                      onClick={() => handleFruitClick(fruit)}
                      className="w-full flex items-center justify-between p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer group"
                      data-testid={`fruit-${fruit.id}`}
                    >
                      <span className="text-2xl">{fruit.emoji}</span>
                      <div className="flex-1 mx-3 text-left">
                        <div className="text-sm text-white capitalize">{fruit.name}</div>
                        <Progress value={fruit.progressPercent || 0} className="h-1.5 mt-1 bg-slate-700" />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-orange-400">Lv.{fruit.level || 1}</span>
                        <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors" />
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="text-sm text-slate-400 text-center py-2">
                    Play levels to unlock fruit mastery!
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Quick Actions - Daily Spin, Battle Pass, Cosmetics */}
          <div className="p-4 space-y-2 border-b border-white/10">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Rewards & Customization</p>
            
            {/* Daily Spin */}
            <MenuButton 
              icon={<RotateCw className="w-5 h-5" />}
              label="Daily Spin"
              onClick={() => {
                onClose();
                onMenuSelect?.('daily-spin');
              }}
              badge={canSpin ? "FREE" : null}
              highlight={canSpin}
            />
            
            {/* Battle Pass */}
            <MenuButton 
              icon={<Gift className="w-5 h-5" />}
              label="Battle Pass"
              onClick={() => {
                onClose();
                onMenuSelect?.('battle-pass');
              }}
            />
            
            {/* Cosmetics */}
            <MenuButton 
              icon={<Palette className="w-5 h-5" />}
              label="Cosmetics"
              onClick={() => {
                onClose();
                onMenuSelect?.('cosmetics');
              }}
            />
          </div>
          
          {/* Menu Actions */}
          <div className="p-4 space-y-2">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Settings</p>
            
            <MenuButton 
              icon={<Settings className="w-5 h-5" />}
              label="Settings"
              onClick={() => {
                onClose();
                onMenuSelect?.('settings');
              }}
            />
            <MenuButton 
              icon={<Newspaper className="w-5 h-5" />}
              label="News"
              onClick={() => onMenuSelect?.('news')}
              badge="3"
            />
            <MenuButton 
              icon={<Scale className="w-5 h-5" />}
              label="Legal"
              onClick={() => setShowLegal(!showLegal)}
            />
          </div>
        </div>
        
        {/* Logout - Fixed at bottom */}
        <div className="p-4 border-t border-white/10 flex-shrink-0">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-red-400 hover:text-red-300 hover:bg-red-500/10"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </Button>
        </div>
      </aside>
    </>
  );
}

// Stat Card Component
function StatCard({ icon, value, label, color, small = false }) {
  return (
    <div className={cn(
      "rounded-xl bg-gradient-to-br border border-white/10",
      color,
      small ? "p-2" : "p-2"
    )}>
      <div className="flex items-center gap-1.5">
        {icon}
        <span className={cn(
          "font-bold text-white",
          small ? "text-xs" : "text-sm"
        )}>
          {value}
        </span>
      </div>
      <span className={cn(
        "text-slate-400 mt-0.5 block",
        "text-[10px]"
      )}>
        {label}
      </span>
    </div>
  );
}

// Menu Button Component
function MenuButton({ icon, label, onClick, badge, highlight = false }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all duration-200",
        highlight 
          ? "text-white bg-gradient-to-r from-orange-500/20 to-yellow-500/20 border border-orange-500/30 hover:from-orange-500/30 hover:to-yellow-500/30" 
          : "text-slate-300 hover:text-white hover:bg-white/10"
      )}
    >
      <span className={highlight ? "text-orange-400" : "text-slate-400"}>{icon}</span>
      <span className="flex-1 font-medium">{label}</span>
      {badge && (
        <span className={cn(
          "px-2 py-0.5 rounded-full text-xs font-bold",
          highlight ? "bg-orange-500 text-white animate-pulse" : "bg-red-500 text-white"
        )}>
          {badge}
        </span>
      )}
    </button>
  );
}
